import Material from '../../../../../types/api/material'
import OrderRequest from '../../../../../types/api/order-request'
import Shipment from '../../../../../types/api/shipment'
import { MaterialRequirement } from '../../../../../types/api/components/material/material-requirements'
import { getDisplayFromReceipt, matchesShipmentRelation } from './common'
import { AvailabilityDisplay } from './types'

export interface MaterialAvailabilityItem {
    label: string
    display: AvailabilityDisplay
}

interface OrderMaterialItem {
    label: string
    material: Material
}

function orderRequestsForShipment(material: Material, shipment: Shipment): OrderRequest[] {
    return (material.order_requests || []).filter(
        request =>
            request.requestStatus !== 'Отменен' &&
            matchesShipmentRelation(request.shipment, shipment)
    )
}

function orderRequestToDisplay(orderRequest: OrderRequest): AvailabilityDisplay {
    if (orderRequest.requestStatus === 'Обработан') {
        const receipt = orderRequest.receipt_position?.receipt
        return receipt ? getDisplayFromReceipt(receipt) : { problem: 'Что-то пошло не так' }
    }

    if (orderRequest.requestStatus === 'Новый') {
        return { problem: `Создана заявка на заказ №${orderRequest.id}` }
    }

    return { problem: 'Что-то пошло не так' }
}

function getMaterialLabel(requirement: MaterialRequirement, material: Material): string {
    const typeName =
        requirement.material_type?.name || material.material_type?.name || 'Материал'

    if (material.width != null) {
        return `${typeName} (${material.width} мм)`
    }

    return typeName
}

function collectOrderMaterials(shipment: Shipment): OrderMaterialItem[] {
    const byDocumentId = new Map<string, OrderMaterialItem>()

    for (const requirement of shipment.materialRequirements || []) {
        for (const config of requirement.configurations || []) {
            if (config.action !== 'Заказать' || !config.amount || !config.material?.documentId) {
                continue
            }

            const material = config.material
            byDocumentId.set(material.documentId, {
                material,
                label: getMaterialLabel(requirement, material),
            })
        }
    }

    return [...byDocumentId.values()]
}

/** Статусы по каждому заказанному материалу (для tooltip и popover) */
export function getOrderedMaterialAvailabilityItems(
    shipment?: Shipment | null
): MaterialAvailabilityItem[] {
    if (!shipment) {
        return [{ label: 'Материал', display: { problem: 'Что-то пошло не так' } }]
    }

    const materials = collectOrderMaterials(shipment)

    return materials.flatMap(({ material, label }) => {
        const requests = orderRequestsForShipment(material, shipment)

        if (requests.length === 0) {
            return [{ label, display: { problem: 'Материал не заказан' } }]
        }

        return requests.map(request => ({
            label,
            display: orderRequestToDisplay(request),
        }))
    })
}

function formatItemMessage({ label, display }: MaterialAvailabilityItem): string | null {
    if (display.waiting) {
        return `${label}: ${display.waiting}`
    }

    if (display.problem) {
        return `${label}: ${display.problem}`
    }

    return null
}

export function getMaterialAvailabilityStatus(shipment?: Shipment | null): AvailabilityDisplay {
    if (!shipment) {
        return { problem: 'Что-то пошло не так' }
    }

    const items = getOrderedMaterialAvailabilityItems(shipment)

    if (items.length === 0) {
        return { inStock: true }
    }

    const problemMessages = items
        .filter(item => item.display.problem)
        .map(formatItemMessage)
        .filter((message): message is string => Boolean(message))
    const waitingMessages = items
        .filter(item => item.display.waiting)
        .map(formatItemMessage)
        .filter((message): message is string => Boolean(message))

    // Иконка по худшему статусу, в tooltip — каждый материал отдельно
    if (problemMessages.length > 0) {
        return { problem: [...problemMessages, ...waitingMessages].join('\n') }
    }

    if (waitingMessages.length > 0) {
        return { waiting: waitingMessages.join('\n') }
    }

    return { inStock: true }
}
